import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useEffect, useRef } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { gsap } from '@/lib/gsap'
import { useBooking } from '@/components/landing/booking-provider'
import FloorPlan from '@/components/landing/floor-plan'
import { FLEET_COPY, SPECS } from '@/constants/aerive'

// The aircraft: the black jet climbs up against the scroll, settles, and its fuselage
// turns into a champagne cabin plan while the specifications arrive around it.
const Fleet: FC = (): ReactNode => {
    const { openBooking } = useBooking()
    const sectionRef = useRef<HTMLElement>(null)
    const stageRef = useRef<HTMLDivElement>(null)
    const headlineRef = useRef<HTMLHeadingElement>(null)
    const jetBoxRef = useRef<HTMLDivElement>(null)
    const jetImageRef = useRef<HTMLDivElement>(null)
    const leftRef = useRef<HTMLDivElement>(null)
    const rightRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const section = sectionRef.current
        const stage = stageRef.current
        const jetBox = jetBoxRef.current
        if (!section || !stage || !jetBox) return

        const plan = jetBox.querySelector<SVGElement>('[data-floor-plan]')
        const strokes = jetBox.querySelectorAll<SVGPathElement>('[data-plan-stroke]')
        const lines = section.querySelectorAll<HTMLElement>('[data-fleet-line]')
        const leftItems = leftRef.current?.querySelectorAll<HTMLElement>('[data-fleet-item]') ?? []
        const rightItems = rightRef.current?.querySelectorAll<HTMLElement>('[data-fleet-item]') ?? []

        const mm = gsap.matchMedia()

        mm.add({
            motion: '(prefers-reduced-motion: no-preference)',
            reduce: '(prefers-reduced-motion: reduce)',
            mobile: '(max-width: 767px)',
            desktop: '(min-width: 768px)'
        }, (context) => {
            const { motion, mobile } = context.conditions as { motion: boolean; mobile: boolean }

            const settled = mobile
                ? { y: () => -window.innerHeight * 0.22, scale: 0.55 }
                : { y: 0, scale: 0.88 }

            const timeline = gsap.timeline({
                defaults: { ease: 'none' },
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: () => `+=${Math.round(window.innerHeight * 3.2)}`,
                    scrub: 1,
                    pin: stage,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                    refreshPriority: 7
                }
            })

            if (motion) {
                timeline
                    .fromTo(jetBox, { y: () => window.innerHeight * 0.95, rotation: -9, scale: 1.12 }, { y: 0, rotation: 0, scale: 1, ease: 'power2.out', duration: 4 }, 0)
                    .fromTo(lines, { y: 0 }, { y: () => window.innerHeight * 0.14, stagger: 0.3, duration: 3.6 }, 0)
                    .to(headlineRef.current, { autoAlpha: 0, duration: 1.2 }, 3.2)
                    .to(jetImageRef.current, { autoAlpha: 0.1, duration: 1.6 }, 4.3)
                    .fromTo(plan, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5 }, 4.3)
                    .fromTo(strokes, { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 1.2, stagger: 0.03 }, 4.4)
                    .to(jetBox, { ...settled, ease: 'power2.inOut', duration: 1.8 }, 6.4)
                    .fromTo(leftItems, { autoAlpha: 0, y: 40 }, { autoAlpha: 1, y: 0, ease: 'power2.out', stagger: 0.12, duration: 1.2 }, 6.7)
                    .fromTo(rightItems, { autoAlpha: 0, y: 40 }, { autoAlpha: 1, y: 0, ease: 'power2.out', stagger: 0.12, duration: 1.2 }, 7)
                    .to({}, { duration: 1 })
            } else {
                gsap.set(jetBox, settled)
                timeline
                    .to(headlineRef.current, { autoAlpha: 0, duration: 1.5 }, 1)
                    .to(jetImageRef.current, { autoAlpha: 0.1, duration: 1.5 }, 2)
                    .fromTo(plan, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.5 }, 2)
                    .fromTo(leftItems, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.5 }, 3.5)
                    .fromTo(rightItems, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.5 }, 3.5)
                    .to({}, { duration: 1 })
            }
        })

        return () => mm.revert()
    }, [])

    return (
        <section id='fleet' ref={sectionRef} aria-labelledby='fleet-title' className='relative'>
            <div ref={stageRef} className='stage relative isolate w-full overflow-hidden bg-ivory text-obsidian'>
                <div className='stage-safe absolute inset-x-0 top-0'>
                    <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_58%,rgba(255,255,255,0.75)_0%,rgba(243,236,224,0)_62%)]' />

                    <h2
                        id='fleet-title'
                        ref={headlineRef}
                        className='pointer-events-none absolute inset-x-5 top-[13%] font-heading text-[clamp(4rem,14vw,15rem)] font-light leading-[0.84] tracking-[-0.02em] text-obsidian md:inset-x-[6vw] md:top-[14%]'
                    >
                        <span data-fleet-line className='block'>{FLEET_COPY.headline[0]}</span>
                        <span data-fleet-line className='block text-right'>{FLEET_COPY.headline[1]}</span>
                    </h2>

                    <div
                        ref={jetBoxRef}
                        className='jet-box absolute left-1/2 top-1/2 mix-blend-multiply will-change-transform'
                        style={{ marginLeft: 'calc(var(--jw) * -0.5)', marginTop: 'calc(var(--jw) * -0.6207)' }}
                    >
                        <div ref={jetImageRef} className='absolute inset-0'>
                            <Image
                                src='/images/aerive/jet-top.png'
                                alt='The AERIVE flagship, a black long range jet with a champagne line, seen from above'
                                fill
                                sizes='(max-width: 767px) 118vw, 69vh'
                                quality={90}
                                loading='eager'
                                className='object-contain'
                            />
                        </div>
                        <FloorPlan className='invisible absolute inset-0 h-full w-full text-gilt-deep opacity-0' />
                    </div>

                    <div
                        ref={leftRef}
                        className='absolute inset-x-5 bottom-[12%] md:bottom-auto md:left-[6vw] md:right-auto md:top-[15%] md:w-[26vw] md:max-w-[26rem]'
                    >
                        <p data-fleet-item className='invisible text-[11px] uppercase tracking-[0.25em] text-umber opacity-0'>
                            {FLEET_COPY.eyebrow}
                        </p>
                        <p data-fleet-item className='invisible mt-3 font-heading text-[clamp(2.4rem,4.4vw,4.6rem)] font-light leading-[0.95] opacity-0'>
                            {FLEET_COPY.title}
                        </p>
                        <dl data-fleet-item className='invisible mt-6 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-obsidian/15 pt-5 opacity-0 md:mt-10 md:gap-y-6 md:pt-7'>
                            {SPECS.map((spec) => (
                                <div key={spec.label}>
                                    <dt className='text-[10px] uppercase tracking-[0.22em] text-ash'>{spec.label}</dt>
                                    <dd className='mt-1.5 text-[13px] font-medium leading-snug text-obsidian md:text-[14px]'>{spec.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>

                    <div ref={rightRef} className='absolute right-[6vw] top-[15%] hidden w-[23vw] max-w-[22rem] md:block'>
                        <p data-fleet-item className='invisible font-heading text-[clamp(1.7rem,2.3vw,2.4rem)] font-normal leading-[1.1] opacity-0'>
                            {FLEET_COPY.claim}
                        </p>
                        <span data-fleet-item className='invisible my-7 block h-px w-full bg-obsidian/15 opacity-0' />
                        <p data-fleet-item className='invisible text-[10px] uppercase tracking-[0.24em] text-umber opacity-0'>
                            {FLEET_COPY.label}
                        </p>
                        <p data-fleet-item className='invisible mt-4 text-[14px] leading-[1.75] text-ash opacity-0'>
                            {FLEET_COPY.text}
                        </p>
                        <div data-fleet-item className='invisible mt-8 opacity-0'>
                            <button
                                type='button'
                                onClick={openBooking}
                                className='inline-flex h-11 items-center gap-2 rounded-full border border-obsidian/25 px-5 text-[11px] uppercase tracking-[0.22em] text-obsidian transition-colors hover:bg-obsidian hover:text-ivory focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-obsidian'
                            >
                                Fly the flagship
                                <ArrowUpRight className='size-4' />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Fleet
